import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";

import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import AppPageHeader from "components/AppPageHeader";
import AppSearch from "components/AppSearch";
import ErrorBoundary from "components/ErrorBoundary";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import SearchNoResults from "components/SearchNoResults";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import {
  setListHorsesRequestKey,
  setSearchQuery as setStoreSearchQuery
} from "store/actions/trainerHorseListPage";
import HorseList from "./HorseList";

import useStyles from "./styles";

const RECORDS_PER_PAGE = 40;
export const TRAINER_HORSE_LIST_QUERY_KEY = "TRAINER_HORSE_LIST_QUERY_KEY";

const TrainerHorseListsPage = () => {
  const classes = useStyles();

  const { showErrorSnack } = useSnackbar();
  const [clearSearch, setClearSearch] = useState<boolean>(false);

  const { useInfiniteListHorses } = useRacehorse360Api();
  const { currentUser } = useLoggedInUser();
  const tableBodyRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const { searchQuery } = useAppSelector(state => {
    const { searchQuery } = state.trainerHorseListPage;

    return {
      searchQuery
    };
  });

  const {
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    data,
    refetch,
    compoundRequestKey
  } = useInfiniteListHorses(
    TRAINER_HORSE_LIST_QUERY_KEY,
    {
      query: {
        ...currentUser.trainerHorseAndWorkoutFilter,
        name: searchQuery?.length ? { contains: searchQuery } : undefined
      },
      pagingOptions: {
        maxResults: RECORDS_PER_PAGE,
        includeSummary: true
      },
      getOptions: {
        select: [
          "name",
          "birthday",
          "gender",
          "color_description",
          "color",
          "trainer"
        ],
        orderBy: ["name"]
      }
    },
    {
      onError: err => {
        showErrorSnack(err.message);
      }
    }
  );

  const horses: racehorse360.IHorse[] = data?.pages ? data.pages.flat() : [];

  useInfiniteScroll(
    tableBodyRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    true,
    true
  );

  useEffect(() => {
    return () => {
      dispatch(setStoreSearchQuery(""));
    };
  }, []);

  useEffect(() => {
    dispatch(setListHorsesRequestKey(compoundRequestKey));
  }, [JSON.stringify(compoundRequestKey)]);

  const handleClear = useCallback(() => {
    dispatch(setStoreSearchQuery(""));
    setClearSearch(true);
  }, [dispatch]);

  const handleAppSearch = (value: string) => {
    dispatch(setStoreSearchQuery(value));
  };

  return (
    <AppPage>
      <AppPageHeader className={classes.appPageHeader}>
        <Grid container className={classes.topBar}>
          <Grid item xs={12} sm={12} md={4}>
            <Typography className={classes.topBarTitle}>
              Select Horse
            </Typography>
          </Grid>

          <Grid item xs={12} sm={8} md={4}>
            <ErrorBoundary>
              <AppSearch
                isForceSearch
                placeholder={"Search Horses"}
                onSearch={handleAppSearch}
                clearSearch={clearSearch}
                onClearSearch={setClearSearch}
              />
            </ErrorBoundary>
          </Grid>
        </Grid>
      </AppPageHeader>
      {isLoading ? (
        <Loader />
      ) : (
        <AppPageContent className={classes.appPageContent}>
          <ErrorBoundary>
            {horses?.length ? (
              <HorseList
                horses={horses}
                tableBodyRef={tableBodyRef}
                refetchData={refetch}
              />
            ) : (
              <SearchNoResults
                value={searchQuery}
                onClearSearch={handleClear}
              />
            )}
          </ErrorBoundary>
          {isFetchingNextPage && <Loader infinite />}
        </AppPageContent>
      )}
    </AppPage>
  );
};

export default TrainerHorseListsPage;
